import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertTriangle, Shield, Eye, Clock, Activity, Users, Database, Server } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface SecurityEvent {
  id: string
  action: string
  resource_type: string
  severity: string
  source: string
  ip_address: string
  user_id: string
  created_at: string
  details: any
}

interface SecurityStats {
  totalEvents: number
  highSeverity: number
  rateLimitExceeded: number
  suspicious: number
  uniqueIPs: number
  affectedTenants: number
}

const SecurityDashboard = () => {
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([])
  const [rateLimitEvents, setRateLimitEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<SecurityStats>({
    totalEvents: 0,
    highSeverity: 0,
    rateLimitExceeded: 0,
    suspicious: 0,
    uniqueIPs: 0,
    affectedTenants: 0
  })
  const { toast } = useToast()

  useEffect(() => {
    fetchSecurityData()
  }, [])

  const fetchSecurityData = async () => {
    try {
      await Promise.all([
        fetchSecurityEvents(),
        fetchRateLimitEvents(),
        fetchSecurityStats()
      ])
    } catch (error) {
      console.error('Error fetching security data:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Sicherheitsdaten konnten nicht geladen werden"
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchSecurityEvents = async () => {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .in('action', [
        'rate_limit_exceeded',
        'suspicious_activity_detected',
        'login_attempt',
        'signup_attempt', 
        'password_reset_request',
        'security_event'
      ])
      .order('created_at', { ascending: false })
      .limit(100)

    if (error) throw error
    setSecurityEvents(data || [])
  }

  const fetchRateLimitEvents = async () => {
    const { data, error } = await supabase
      .from('rate_limit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50)

    if (error) throw error
    setRateLimitEvents(data || [])
  }

  const fetchSecurityStats = async () => {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('action, severity, ip_address, user_id')
      .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())

    if (error) throw error

    const events = data || []
    const uniqueIPs = new Set(events.map(e => e.ip_address).filter(Boolean)).size
    const uniqueUsers = new Set(events.map(e => e.user_id).filter(Boolean)).size

    setStats({
      totalEvents: events.length,
      highSeverity: events.filter(e => e.severity === 'high').length,
      rateLimitExceeded: events.filter(e => e.action === 'rate_limit_exceeded').length,
      suspicious: events.filter(e => e.action === 'suspicious_activity_detected').length,
      uniqueIPs,
      affectedTenants: uniqueUsers
    })
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'destructive'
      case 'warning': return 'secondary'
      case 'info': return 'outline'
      default: return 'outline'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-4 w-4" />
      case 'warning': return <Eye className="h-4 w-4" />
      default: return <Activity className="h-4 w-4" />
    }
  }

  const clearOldLogs = async () => {
    try {
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      
      const { error } = await supabase
        .from('rate_limit_logs')
        .delete()
        .lt('created_at', oneWeekAgo)

      if (error) throw error

      toast({
        title: "Erfolg",
        description: "Alte Rate-Limit-Logs wurden bereinigt"
      })
      
      fetchRateLimitEvents()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler", 
        description: "Bereinigung fehlgeschlagen"
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Security Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ereignisse (24h)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEvents}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kritisch</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.highSeverity}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rate Limits</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.rateLimitExceeded}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verdächtig</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.suspicious}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique IPs</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueIPs}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Betroffene Nutzer</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.affectedTenants}</div>
          </CardContent>
        </Card>
      </div>

      {/* Security Alerts */}
      {stats.highSeverity > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Sicherheitswarnung:</strong> {stats.highSeverity} kritische Ereignisse in den letzten 24 Stunden erkannt.
            Sofortige Überprüfung erforderlich.
          </AlertDescription>
        </Alert>
      )}

      {/* Security Events Tabs */}
      <Tabs defaultValue="events" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="events">Sicherheitsereignisse</TabsTrigger>
          <TabsTrigger value="ratelimits">Rate Limiting</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Systemweite Sicherheitsereignisse</CardTitle>
                <Button onClick={fetchSecurityData} variant="outline" size="sm">
                  Aktualisieren
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Lädt...</div>
              ) : securityEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Keine Sicherheitsereignisse gefunden
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {securityEvents.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        {getSeverityIcon(event.severity)}
                        <div>
                          <div className="font-medium">{event.action}</div>
                          <div className="text-sm text-muted-foreground">
                            {event.ip_address || 'Unbekannte IP'} • {new Date(event.created_at).toLocaleString()}
                          </div>
                          {event.details && Object.keys(event.details).length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1 max-w-md truncate">
                              {event.details.identifier || JSON.stringify(event.details)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={getSeverityColor(event.severity)}>
                          {event.severity}
                        </Badge>
                        <Badge variant="outline">{event.source || 'system'}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ratelimits" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Rate Limiting Überwachung</CardTitle>
                <div className="flex gap-2">
                  <Button onClick={clearOldLogs} variant="outline" size="sm">
                    Alte Logs bereinigen
                  </Button>
                  <Button onClick={fetchRateLimitEvents} variant="outline" size="sm">
                    Aktualisieren
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {rateLimitEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Keine Rate-Limit-Ereignisse gefunden
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {rateLimitEvents.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium">{event.action}</div>
                        <div className="text-sm text-muted-foreground">
                          {event.identifier} • {event.attempts} Versuche
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(event.created_at).toLocaleString()}
                        </div>
                      </div>
                      <Badge variant="secondary">
                        {event.attempts}/{event.max_attempts || 'N/A'} Versuche
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default SecurityDashboard